import { useState } from "react";
import { toast } from "sonner";
import { Truck, ShieldCheck } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import InsurancePicker from "@/components/InsurancePicker";

const EMPTY = { name: "", phone: "", email: "", address: "", rx_numbers: "" };

export default function Delivery() {
  const [form, setForm] = useState(EMPTY);
  const [insurance, setInsurance] = useState(null);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  async function submit(e) {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/delivery/requests", { ...form, insurance });
      toast.success("Delivery request submitted! We'll be in touch shortly.");
      setForm(EMPTY);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to submit.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="mx-auto max-w-3xl px-6 py-16">
      <div className="mb-8 flex items-center gap-3">
        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-secondary text-primary"><Truck className="h-6 w-6" /></div>
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Prescription Delivery</h1>
          <p className="text-sm text-muted-foreground">Enter your details and the Rx numbers you'd like refilled.</p>
        </div>
      </div>

      <form onSubmit={submit} className="space-y-5 rounded-2xl border border-border bg-card p-6 sm:p-8">
        <div className="grid gap-5 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="name">Full name</Label>
            <Input id="name" data-testid="delivery-name" value={form.name} onChange={set("name")} required />
          </div>
          <div className="space-y-2">
            <Label htmlFor="phone">Phone number</Label>
            <Input id="phone" data-testid="delivery-phone" value={form.phone} onChange={set("phone")} required />
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" data-testid="delivery-email" value={form.email} onChange={set("email")} required />
        </div>
        <div className="space-y-2">
          <Label htmlFor="address">Delivery address</Label>
          <Input id="address" data-testid="delivery-address" value={form.address} onChange={set("address")} required />
        </div>
        <div className="space-y-2">
          <Label htmlFor="rx">Rx numbers to refill</Label>
          <Textarea id="rx" data-testid="delivery-rx" placeholder="e.g. 1234567, 8910111" value={form.rx_numbers} onChange={set("rx_numbers")} required />
        </div>
        <div className="flex items-center gap-2 rounded-lg bg-secondary p-3 text-xs text-secondary-foreground">
          <ShieldCheck className="h-4 w-4 text-primary" /> Your information is transmitted securely and only used to process your request.
        </div>
        <InsurancePicker onChange={setInsurance} testPrefix="delivery" />
        <Button type="submit" disabled={loading} data-testid="delivery-submit" className="w-full rounded-full sm:w-auto sm:px-10">
          {loading ? "Submitting..." : "Submit Delivery Request"}
        </Button>
      </form>
    </div>
  );
}
